import { forwardRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Tooltip } from '@components/Tooltip/Tooltip';
import * as tokens from '@tokens';
import { useInterpolate } from '@hooks/useInterpolate';
import { useStrings } from '@hooks/useStrings';

interface AvatarCommonProps {
  /**
   * User's name. Will be displayed as tooltip. If no `src`, userName initials will be generated as fallback for image.
   */
  userName: string;
  /**
   * Size of the avatar. 4 sizes supported, to result in 16/24/32/48 px.
   * Depending on the size, there is a limit of how many initials can appear: s=1, m=2, l=2, xl=2.
   */
  size?: 's' | 'm' | 'l' | 'xl';
  /**
   * If set to true, adds an outline to the avatar. This should only be used
   * if the avatar is stacked in a group of avatars.
   */
  isStacked?: boolean;
  /**
   * If the avatar should not have a tooltip.
   */
  hideTooltip?: boolean;
  /**
   * Styles override.
   */
  className?: string;
}

interface AvatarImageProps extends AvatarCommonProps {
  /**
   * Path of user avatar image.
   */
  src: string;
  /**
   * Alt text for the avatar image.
   */
  altText?: string;
}

interface AvatarInitialsProps extends AvatarCommonProps {
  src?: undefined;
  altText?: undefined;
}

export type AvatarProps = AvatarInitialsProps | AvatarImageProps;

const SIZES = {
  s: 16,
  m: 24,
  l: 32,
  xl: 48,
};

const INITIALS_SIZES = {
  s: 10,
  m: 12,
  l: 14,
  xl: 16,
};

const INITIALS_LIMIT = {
  s: 1,
  m: 2,
  l: 2,
  xl: 2,
};

interface ContainerProps {
  size: number;
  hasBackground: boolean;
  hasOutline: boolean;
}

interface InitialsProps {
  size: number;
}

const Container = styled('div', {
  shouldForwardProp: (prop: string) => prop === 'children',
})<ContainerProps>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  background-color: ${({ hasBackground }) =>
    hasBackground ? tokens.BRAND_COLORS.neutral_800 : 'transparent'};
  box-shadow: ${({ hasOutline }) =>
    hasOutline ? `0 0 0 1px ${tokens.color.ui_background}` : 'none'};
  border-radius: ${tokens.radius.max};
  font-size: 100%;
  ${tokens.flexCenter()}
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
`;

const Initials = styled('span', {
  shouldForwardProp: (prop: string) => prop !== 'size',
})<InitialsProps>`
  font-size: ${({ size }) => size}px;
  font-weight: 700;
  color: ${tokens.color.text_default_inverse};
`;

/**
 * An avatar is a visual representation of a user or entity.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/843069-avatar/b/2752f5 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-avatar--basic-avatar Code documentation}
 *
 * @example
 * <Avatar userName='John Smith' />
 */
export const Avatar = forwardRef<HTMLElement, AvatarProps>(
  (
    {
      userName,
      src,
      altText,
      size = 's',
      isStacked = false,
      hideTooltip = false,
      className,
    }: AvatarProps,
    ref,
  ) => {
    const [hasError, setError] = useState<boolean>(false);

    const { avatar } = useStrings();
    const interpolate = useInterpolate();

    const finalAltText = altText || interpolate(avatar.altText, { userName });

    useEffect(() => {
      setError(!src);
    }, [src]);

    if (!userName) {
      return null;
    }

    // If there isn't a picture url we show initials instead.
    const showInitials = hasError;

    const initials =
      showInitials &&
      userName
        .toUpperCase()
        .split(' ')
        .slice(0, INITIALS_LIMIT[size])
        .map((word) => word.charAt(0))
        .join('');

    return (
      <Tooltip text={userName} isDisabled={hideTooltip} ref={ref}>
        <Container
          size={SIZES[size]}
          className={className}
          hasBackground={showInitials}
          hasOutline={isStacked}
        >
          {!showInitials && (
            <Image
              data-hj-suppress
              src={src}
              alt={finalAltText}
              onError={() => {
                setError(true);
              }}
            />
          )}
          {showInitials && (
            <Initials
              data-hj-suppress
              aria-label={userName}
              size={INITIALS_SIZES[size]}
            >
              {initials}
            </Initials>
          )}
        </Container>
      </Tooltip>
    );
  },
);

Avatar.displayName = 'Avatar';
